import { Component, OnInit, Input } from '@angular/core';
import { TabComponent } from '../tab-component';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { SupplierRating, RatePurpose, SupplierRatingAttachment } from 'app/profile/models/supplier-rating.model';
import { SupplierProfileState } from 'app/profile/store/app.reducer';
import { CommonService } from 'app/profile/services/common.service';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { I18nService } from 'app/shared/services/i18n.service';
import { TslDialog, TslSort, PageEvent } from '@tesla/ng-tds';
import { MatDialog } from '@angular/material/dialog';
import { getRatePurpose, SelectSupplierProfile, getGeneralSettings } from 'app/profile/store/app.selector';
import { GetPurposes, GetPartList, GetSupplierContactListByCode } from 'app/profile/store/app.actions';
import { SupplierRatingService } from 'app/profile/services/supplier-rating.service';
import { RatingFormDialogComponent } from './rating-form-dialog/rating-form-dialog.component';
import { ProfileService } from 'app/profile/services/profile.service';
import { Profile } from 'app/profile/models/profile.model';
import { GeneralSettings } from 'app/profile/models/generic.model';

@Component({
    templateUrl: './internal.component.html',
    styleUrls: ['./internal.component.scss']
})
export class InternalComponent implements OnInit, TabComponent {
    @Input() data: any;
    supplierCode: string;
    ratePurpose$: Observable<RatePurpose[]>;
    createdUsers: string[] = [];
    supplierRatings: SupplierRating[] = [];
    avgRate: string;
    profile: Profile;

    // table
    filter = { category: 0, createdBy: '', rate: "0", keywordSearch: '' };
    datasource: BehaviorSubject<Array<SupplierRating>> = new BehaviorSubject([]);
    displayedColumns: Array<string> = ["category", "createdBy", "createdOn", "rate", "title", "action"];
    pageIndex = 0;
    pageSize = 10;
    pageSizeOptions: number[] = [10, 25, 50, 100, 250];
    totalRecords = 0;
    helpUrl: string;
    settings: GeneralSettings;

    get showBlackListed() {
        return this.profile.supplierDetails.inactiveDate !== null;
    }

    get isBlackListed() {
        const inactiveDate = new Date(this.profile.supplierDetails.inactiveDate);
        return inactiveDate.getTime() < Date.now();
    }

    get status(): string {
        if (this.profile) {
            switch (this.profile.preferredStatusId) {
                case 1: return this.translateService.get('Approved');
                case 2: return this.translateService.get('Introduce');
                case 3: return this.translateService.get('On Hold');
                case 4: return this.translateService.get('Phase Out');
                case 5: return this.translateService.get('Preferred');
                default: return '';
            }
        }
    }

    constructor(private route: ActivatedRoute,
        private commonService: CommonService,
        private store: Store<SupplierProfileState>,
        private translate: I18nService,
        private dialog: TslDialog,
        private matDialog: MatDialog,
        private service: SupplierRatingService,
        private profileService: ProfileService,
        public translateService: I18nService
    ) { }

    ngOnInit() {
        this.store.select(getGeneralSettings).subscribe(res => {
            this.settings = res;
            if (res) {
                this.helpUrl = this.settings.settings.internalHelpUrl;
            }
        });
        this.route.parent.params.subscribe(params => {
            if (!params["code"]) {
                return;
            }

            this.supplierCode = this.commonService.getSupplierCode(params["code"]);
            if (this.supplierCode !== null) {
                this.loadResources();
            }
        });
    }

    isDirty(): boolean {
        return false;
    }

    onPageChange(pageChange: PageEvent): void {
        const { pageIndex, pageSize } = pageChange;
        this.pageIndex = pageIndex;
        this.pageSize = pageSize;
        this.onFilter();
    }

    onSortChange(sort: TslSort): void {
        const orderedItems = this.supplierRatings.sort((a, b) => {
            const isAsc = sort.direction === 'asc';

            switch (sort.active) {
                case 'purpose': return compare(a.ratePurpose, b.ratePurpose, isAsc);
                case 'createdBy': return compare(a.createdBy, b.createdBy, isAsc);
                case 'createdOn': return compare(a.createdOn.getTime(), b.createdOn.getTime(), isAsc);
                case 'rate': return compare(a.rate, b.rate, isAsc);
                case 'subject': return compare(a.subject, b.subject, isAsc);
                default: return 0;
            }
        });

        const skip = this.pageIndex * this.pageSize;
        const take = (this.pageIndex + 1) * this.pageSize;
        this.datasource.next(orderedItems.slice(skip, take));

        function compare(a: number | string, b: number | string, isAsc: boolean) {
            return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
        }
    }

    onFilter(): void {
        let items = this.supplierRatings;

        if (this.filter.category > 0) {
            items = items.filter(x => x.ratePurposeId === this.filter.category);
        }

        if (this.filter.createdBy !== '') {
            items = items.filter(x => x.createdBy === this.filter.createdBy);
        }

        if (parseInt(this.filter.rate) > 0) {
            items = items.filter(x => x.rate === parseInt(this.filter.rate));
        }

        if (this.filter.keywordSearch.length > 0) {
            const keyword = this.filter.keywordSearch.toLowerCase();
            items = items.filter(x => x.subject.toLowerCase().includes(keyword) || x.note.toLowerCase().includes(keyword));
        }

        const skip = this.pageIndex * this.pageSize;
        const take = (this.pageIndex + 1) * this.pageSize;
        this.datasource.next(items.slice(skip, take));
        this.totalRecords = items.length;
    }

    onClearFilter(): void {
        this.filter = { category: 0, createdBy: '', rate: '0', keywordSearch: '' };
        this.pageIndex = 0;
        this.pageSize = this.pageSizeOptions[0];
        this.onFilter();
    }

    onAdd(): void {
        const model = new SupplierRating();
        model.supplierCode = this.supplierCode;
        this.openSupplierRatingDialog(model);
    }

    onView(model: SupplierRating): void {
        this.openSupplierRatingDialog(model);
    }

    getStars(rate: number): number[] {
        return new Array<number>(rate);
    }

    private openSupplierRatingDialog(model: SupplierRating): void {
        const dialogRef = this.matDialog.open(RatingFormDialogComponent, {
            width: "800px",
            height: "550px",
            data: model
        });

        dialogRef.afterClosed()
            .subscribe(res => {
                if (res === undefined) {
                    return;
                }

                const data: SupplierRating = res;
                if (data.id === undefined || data.id === 0) {
                    if (data.newFiles.length > 0) {
                        forkJoin(data.newFiles.map(file => this.profileService.postGenericAttachment(this.supplierCode, file)))
                            .subscribe(attachs => {
                                data.attachments = attachs.map(a => {
                                    return { fileName: a.name, storageKey: a.key, id: 0, supplierRatingId: 0 };
                                });
                                this.service.create(data)
                                    .subscribe(_ => {
                                        this.loadTable();
                                    });
                            });
                    } else {
                        this.service.create(data)
                            .subscribe(_ => {
                                this.loadTable();
                            });
                    }
                }
            });
    }

    private loadTable(): void {
        this.service.get(this.supplierCode)
            .subscribe(res => {
                this.supplierRatings = res;
                this.onFilter();
                this.getAvg();
                this.getCreatedUsers();
            });
    }

    private getAvg(): void {
        if (this.supplierRatings.length > 0) {
            const rates = this.supplierRatings.map(r => r.rate);
            this.avgRate = (rates.reduce((acc, current) => acc + current) / rates.length).toFixed(2);
        }
    }

    private getCreatedUsers(): void {
        const users: string[] = [];
        this.supplierRatings.map(user => {
            if (users.indexOf(user.createdBy) === -1) {
                users.push(user.createdBy);
            }
        });
        this.createdUsers = users;
    }

    private loadResources(): void {
        this.store.dispatch(new GetPurposes());
        this.store.dispatch(new GetPartList(this.supplierCode));
        this.store.dispatch(new GetSupplierContactListByCode(this.supplierCode));

        this.store.select(SelectSupplierProfile).subscribe(res => this.profile = res);
        this.ratePurpose$ = this.store.select(getRatePurpose);
        this.loadTable();
    }
}
